<script lang="ts">
	//UI
	import { DefaultLoading, Card, Pagenation } from "@hitit/new_ui";
	import OrderList from "./OrderList.svelte";

	//Utils
	import { totalPages as totalPageInfo } from "@hitit/new_ui/src/Common/Pagenation.svelte";
	import { Cach } from "@hitit/hub/src/v7/class/index";
	import { intervalToUTC } from "@hitit/hub/src/v7/utils";
	import { t, api } from "@hitit/hub/src/stores";
	import { createEventDispatcher } from "svelte";
	//Types
	import type { OrderData } from "@hitit/api";

	//Props
	export let loading = false;
	export let storeId = "";
	export let presentPage = 0;
	export let range = {
		start: new Date(),
		end: new Date(),
	};

	//Constants
	const dispatch = createEventDispatcher();
	const cach = new Cach();
	//Variables
	let totalPages = 0;
	let orderData: OrderData[] = [];
	let totalCount = 0;
	//Functions
	async function loadOrders(qRange: Interval, qStoreId: string, qPage = 0) {
		try {
			loading = true;

			const divideCount = 10;
			const { startUTC, endUTC } = intervalToUTC(qRange);
			const currentPageId = `${storeId}_${startUTC}_${endUTC}_${qPage}`;
			const firstOrderResponse = await getFirstOrderResponse(qRange, qStoreId);
			const __totalCount = firstOrderResponse.totalElements;
			const __totalPages = firstOrderResponse.totalPages;
			const { pages } = totalPageInfo(__totalPages, qPage, divideCount);

			getAroundCurrentPageResponse(qRange, qStoreId, pages, qPage);

			const currentPageOrderData =
				cach.getCach(currentPageId) !== null
					? cach.getCach(currentPageId)
					: (
							await $api.listStoreOrders({
								createdDateTimeStartUTC: startUTC,
								createdDateTimeEndUTC: endUTC,
								currentPage: qPage,
								storeid: qStoreId,
							})
					  ).data.orderMinDTOs || [];
			totalCount = __totalCount;
			totalPages = __totalPages;
			orderData = currentPageOrderData.map(storeIdMapCallBack(qStoreId));
		} catch (e) {
			console.log(e);
			console.log("error");
		} finally {
			loading = false;
		}
	}

	async function getAroundCurrentPageResponse(
		qRange: Interval,
		qStoreId: string,
		pages: number[],
		withoutPage: number
	) {
		const { startUTC, endUTC } = intervalToUTC(qRange);
		const emptyCachPages = pages.filter((pageNumber) => {
			const cachId = `${storeId}_${startUTC}_${endUTC}_${pageNumber}`;
			return cach.getCach(cachId) === null && pageNumber !== withoutPage;
		});
		const orderPromises = emptyCachPages.map((v) =>
			$api.listStoreOrders({
				createdDateTimeStartUTC: startUTC,
				createdDateTimeEndUTC: endUTC,
				currentPage: v,
				storeid: qStoreId,
			})
		);
		const responses = await Promise.all(orderPromises);

		for (let response of responses) {
			const cachId = `${storeId}_${startUTC}_${endUTC}_${response.data.currentPage}`;
			const dtos = (response.data.orderMinDTOs || []).map(
				storeIdMapCallBack(storeId)
			);
			cach.setCach(cachId, dtos);
		}
	}

	async function getFirstOrderResponse(qRange: Interval, qStoreId: string) {
		const { startUTC, endUTC } = intervalToUTC(qRange);
		const id = `FIRST_RESPONSE_${qStoreId}_${startUTC}_${endUTC}_${0}`;
		const firstResponse = cach.getCach(id);

		if (firstResponse) {
			return firstResponse;
		} else {
			const currentPageId = `${storeId}_${startUTC}_${endUTC}_${0}`;
			const firstResponse = await $api.listStoreOrders({
				createdDateTimeStartUTC: startUTC,
				createdDateTimeEndUTC: endUTC,
				currentPage: 0,
				storeid: qStoreId,
			});
			firstResponse.data.orderMinDTOs = firstResponse.data.orderMinDTOs.map(
				storeIdMapCallBack(qStoreId)
			);
			cach.setCach(id, firstResponse.data);
			cach.setCach(currentPageId, firstResponse.data.orderMinDTOs || []);
			return firstResponse.data;
		}
	}
	function buttonCallback(page: number) {
		dispatch("PAGE", page);
	}

	function storeIdMapCallBack(storeid: string) {
		return (v: OrderData) => {
			return Object.assign({}, v, { storeid });
		};
	}

	//Do
	$: loadOrders(range, storeId, presentPage);
</script>

<Card noPadding>
	<div class="container">
		<div class="header with-padding">
			<span id="title">{$t("hub.revenue.order_list")}</span>
			<span id="count"
				>{$t("global.total")}{totalCount}{$t("global.count_2")}</span
			>
		</div>
		{#if loading}
			<div class="loading">
				<DefaultLoading />
			</div>
		{:else}
			<div class="table">
				<OrderList orderDatas={orderData} />
			</div>
			<div>
				<Pagenation
					buttonCallback={buttonCallback}
					presentPage={presentPage}
					totalPage={totalPages}
				/>
			</div>
		{/if}
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";
	//Mobile First
	div {
		box-sizing: border-box;
	}
	.loading {
		width: 30px;
		height: 30px;
		display: flex;
		justify-content: center;
		align-items: center;
		margin: 0 auto;
	}

	.container {
		display: flex;
		flex-direction: column;
		gap: 20px;
		width: 100%;
		padding: 20px 0px;
	}

	.header {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		color: #46474b;
		font-size: 1.25rem;
		padding-left: 10px;
		padding-right: 10px;

		#title {
			font-weight: 600;
		}

		#count {
			font-weight: 500;
		}
	}

	@include DesktopSize {
		.container {
			gap: 30px;
			padding: 30px 0px;
		}
		.header {
			padding-left: 30px;
			padding-right: 30px;

			#title {
				font-size: 2.125rem;
			}

			#count {
				font-size: 1.5rem;
			}
		}
	}
</style>
